// src/components/sections/ContactSection.js
import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin } from 'lucide-react';

/**
 * ContactSection component displays contact information and a form.
 * @return {React.ReactElement} The ContactSection component.
 */
const ContactSection = () => {
  const nameRef = useRef(null);
  const phoneRef = useRef(null);
  const questionRef = useRef(null);
  const [errors, setErrors] = useState({});

  /**
   * Formats the phone number input as the user types.
   * @param {React.ChangeEvent<HTMLInputElement>} event The input change event object.
   */
  const formatPhoneNumber = (event) => {
    let inputDigits = event.target.value.replace(/\D/g, '');

    // Automatically set the country code to +7
    if (inputDigits.length > 0) {
      if (inputDigits.startsWith('8')) {
        inputDigits = '7' + inputDigits.substring(1);
      } else if (!inputDigits.startsWith('7')) {
        inputDigits = '7' + inputDigits;
      }
    }
    inputDigits = inputDigits.substring(0, 11);

    // Apply the phone number mask
    let formattedValue = '';
    if (inputDigits.length > 0) {
      formattedValue = '+' + inputDigits.substring(0, 1);
      if (inputDigits.length > 1) {
        formattedValue += ' (' + inputDigits.substring(1, 4);
        if (inputDigits.length >= 4) {
          formattedValue += ')';
          if (inputDigits.length > 4) {
            formattedValue += ' - ' + inputDigits.substring(4, 7);
            if (inputDigits.length > 7) {
              formattedValue += ' - ' + inputDigits.substring(7, 9);
              if (inputDigits.length > 9) {
                formattedValue += ' - ' + inputDigits.substring(9, 11);
              }
            }
          }
        }
      }
    }
    event.target.value = formattedValue;
  };

  /**
   * Validates the form fields.
   * @return {boolean} True if the form is valid, false otherwise.
   */
  const validateForm = () => {
    const newErrors = {};
    const name = nameRef.current ? nameRef.current.value.trim() : '';
    const phone = phoneRef.current ? phoneRef.current.value.trim() : '';
    const question = questionRef.current ? questionRef.current.value.trim() : '';

    if (!name) newErrors.name = 'Пожалуйста, введите ваше имя.';

    const phonePattern = /^\+7 \(\d{3}\) - \d{3} - \d{2} - \d{2}$/;
    if (!phone) {
      newErrors.phone = 'Пожалуйста, введите ваш номер телефона.';
    } else if (!phonePattern.test(phone)) {
      newErrors.phone = 'Неверный формат телефона. Используйте формат +7 (XXX) - XXX - XX - XX.';
    }

    if (!question) newErrors.question = 'Пожалуйста, введите ваш вопрос.';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handles form submission by constructing and opening a WhatsApp message link.
   */
  const handleFormSubmit = () => {
    if (!validateForm()) return;

    const name = nameRef.current.value;
    const phone = phoneRef.current.value;
    const question = questionRef.current.value;

    const message = `Здравствуйте, меня зовут ${encodeURIComponent(
      name
    )}%0AДля связи со мной хочу оставить свой номер: ${encodeURIComponent(
      phone
    )}%0AМой вопрос: ${encodeURIComponent(question)}`;

    // Clear fields and errors after submission
    if (nameRef.current) nameRef.current.value = '';
    if (phoneRef.current) phoneRef.current.value = '';
    if (questionRef.current) questionRef.current.value = '';
    setErrors({});

    window.open(`https://wa.me/77785929644?text=${message}`, '_blank');
  };

  return (
    <section id="контакты" className="py-16 bg-white">
      <div className="container mx-auto px-6">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
            Свяжитесь с <span className="text-orange-500">НАМИ</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Готовы ответить на Ваши вопросы и помочь с выбором подходящих строп
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Information Block */}
          <motion.div
            className="space-y-8"
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center gap-4">
              <div className="bg-orange-500 w-12 h-12 rounded-xl flex items-center justify-center">
                <Phone className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-800">Телефон</h3>
                <a href="tel:+77785929644" className="text-orange-500 hover:text-orange-600">
                  +7 (778) 592 9644
                </a>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="bg-orange-500 w-12 h-12 rounded-xl flex items-center justify-center">
                <Mail className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-800">Email</h3>
                <a
                  href="mailto:info@integra-n.kz"
                  className="text-orange-500 hover:text-orange-600"
                >
                  info@integra-n.kz
                </a>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="bg-orange-500 w-12 h-12 rounded-xl flex items-center justify-center">
                <MapPin className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-800">Адрес</h3>
                <p className="text-gray-600">Актау, Казахстан</p>
              </div>
            </div>
          </motion.div>

          {/* Consultation Form Block */}
          <motion.div
            className="bg-gray-50 rounded-2xl p-8 border border-gray-200"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.5 }}
          >
            <h3 className="text-2xl font-bold text-gray-800 mb-6">Быстрая консультация</h3>
            <div className="space-y-6">
              <div>
                <input
                  type="text"
                  ref={nameRef}
                  placeholder="Ваше имя"
                  className={`w-full px-4 py-3 bg-white border rounded-lg focus:outline-none focus:border-orange-500 ${
                    errors.name ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
              </div>
              <div>
                <input
                  type="tel"
                  ref={phoneRef}
                  placeholder="Телефон (+7 (XXX) - XXX - XX - XX)"
                  className={`w-full px-4 py-3 bg-white border rounded-lg focus:outline-none focus:border-orange-500 ${
                    errors.phone ? 'border-red-500' : 'border-gray-300'
                  }`}
                  onChange={formatPhoneNumber}
                  maxLength="24"
                />
                {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
              </div>
              <div>
                <textarea
                  ref={questionRef}
                  placeholder="Ваш вопрос"
                  rows={4}
                  className={`w-full px-4 py-3 bg-white border rounded-lg resize-none focus:outline-none focus:border-orange-500 ${
                    errors.question ? 'border-red-500' : 'border-gray-300'
                  }`}
                ></textarea>
                {errors.question && <p className="text-red-500 text-sm mt-1">{errors.question}</p>}
              </div>
              <button
                onClick={handleFormSubmit}
                className="w-full bg-orange-500 text-white py-3 rounded-lg font-semibold text-center hover:bg-orange-600 transition-colors"
              >
                Отправить в WhatsApp
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
